import React, { useEffect, useRef, useState } from 'react';

/**
 * AudioVisualizer - Real-time audio level visualization component
 * Shows animated bars representing microphone input levels
 */
const AudioVisualizer = ({ 
  audioLevel = 0, 
  isRecording = false, 
  size = 'medium' // 'small', 'medium', 'large'
}) => {
  const [waveformPaths, setWaveformPaths] = useState(['', '', '']);
  const animationRef = useRef(null);
  const lastUpdateRef = useRef(0);
  const phaseRef = useRef([0, 0, 0]);

  // Generate smooth waveform paths that travel right to left
  const generateWaveformPath = (amplitude, frequency, phase, width, height) => {
    const points = 100;
    const centerY = height / 2;
    let path = `M 0 ${centerY}`;
    
    for (let i = 0; i <= points; i++) {
      const x = (i / points) * width;
      // Left to right wave movement
      const waveY = Math.sin((i / points) * frequency + phase) * amplitude * centerY;
      const y = centerY + waveY;
      path += ` L ${x} ${y}`;
    }
    
    return path;
  };

  // Animate waveforms
  useEffect(() => {
    const updateWaveforms = () => {
      const now = Date.now();
      
      if (now - lastUpdateRef.current >= 16) { // 60fps
        // More pronounced amplitude changes - base level higher, bigger multiplier
        const normalizedLevel = isRecording ? Math.min(1, Math.max(0.3, audioLevel * 3)) : 0.4;
        
        // Update phases for smooth right-to-left animation
        phaseRef.current[0] += 0.15;
        phaseRef.current[1] += 0.12;
        phaseRef.current[2] += 0.18;
        
        const { width, height } = getSvgDimensions();
        
        const newPaths = [
          // Primary waveform - most pronounced
          generateWaveformPath(
            normalizedLevel * 1.2, // Increased amplitude
            Math.PI * 4,
            phaseRef.current[0],
            width,
            height
          ),
          // Secondary waveform - medium amplitude
          generateWaveformPath(
            normalizedLevel * 0.9, // Increased amplitude
            Math.PI * 6,
            phaseRef.current[1],
            width,
            height
          ),
          // Harmonic waveform - complementary
          generateWaveformPath(
            normalizedLevel * 1.0, // Increased amplitude
            Math.PI * 5,
            phaseRef.current[2],
            width,
            height
          )
        ];
        
        setWaveformPaths(newPaths);
        lastUpdateRef.current = now;
      }
      
      animationRef.current = requestAnimationFrame(updateWaveforms);
    };

    updateWaveforms();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [audioLevel, isRecording]);

  // Dynamic colors for the new wave entering from right
  const getEntryColor = (waveIndex) => {
    const volumeIntensity = Math.min(1, audioLevel * 2);
    
    const baseHues = [220, 160, 270]; // Blue, Green, Purple
    const redShift = volumeIntensity * 60; // Shift towards red (lower hue)
    
    return `hsl(${baseHues[waveIndex] - redShift}, ${70 + (volumeIntensity * 30)}%, ${50 + (volumeIntensity * 20)}%)`;
  };

  const getSvgDimensions = () => {
    switch (size) {
      case 'small':
        return { width: 120, height: 60 };
      case 'large':
        return { width: 240, height: 120 };
      default:
        return { width: 200, height: 80 };
    }
  };

  const { width, height } = getSvgDimensions();

  return (
    <div className={`audio-visualizer ${size}`}>
      <svg 
        className="waveform-svg"
        width={width} 
        height={height}
        viewBox={`0 0 ${width} ${height}`}
      >
        <defs>
          {/* Edge blur masks */}
          <linearGradient id="edgeFade" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopOpacity="0" />
            <stop offset="15%" stopOpacity="1" />
            <stop offset="85%" stopOpacity="1" />
            <stop offset="100%" stopOpacity="0" />
          </linearGradient>
          
          {waveformPaths.map((_, index) => (
            <linearGradient key={`gradient-${index}`} id={`waveGradient-${index}`} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={getEntryColor(index)} stopOpacity="0.3" />
              <stop offset="20%" stopColor={getEntryColor(index)} stopOpacity="0.8" />
              <stop offset="80%" stopColor={getEntryColor(index)} stopOpacity="0.8" />
              <stop offset="100%" stopColor={getEntryColor(index)} stopOpacity="0.2" />
            </linearGradient>
          ))}
        </defs>
        
        {waveformPaths.map((path, index) => (
          <path
            key={index}
            d={path}
            stroke={`url(#waveGradient-${index})`}
            strokeWidth={size === 'large' ? 4 : size === 'small' ? 2 : 3}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            mask="url(#edgeFade)"
            style={{
              transition: 'all 0.1s ease'
            }}
          />
        ))}
      </svg>
    </div>
  );
};

export default AudioVisualizer;